// Unit detail page. Reached from the property page's Egységek tab (a unit
// card links here). Shows the unit's details read-only by default, with a
// Szerkesztés button that opens the shared unit form; the unit's mérők /
// dokumentumok / tulajdonosok panels live here too (they're per-unit) under a
// tabbed layout. Creating a *new* unit is a separate flow (NewUnitWizardView).

import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { icon, unitIcon } from '../core/icons.js';
import { t } from '../core/i18n.js';
import { formatMoney, formatSize } from '../core/format.js';
import { showFormError, clearFormError } from '../core/formError.js';
import { auth } from '../core/auth.js';
import {
  unitFormHtml, unitDraftFromRow, unitPayloadFromForm, wireUnitFormApartmentOnly,
  wireUnitCurrencySuffix,
} from '../components/UnitCard.js';
import { wireFurnishedRadio, wireEnergyRatingInputs, wireEnergyCertVerify, wireAmenitiesChips, wireAddressFields } from '../components/PropertyBasicsFields.js';
import { attachUnitMetersPanel } from '../components/UnitMetersPanel.js';
import { attachUnitDocumentsPanel } from '../components/UnitDocumentsPanel.js';
import { attachUnitExternalOwnersPanel } from '../components/UnitExternalOwnersPanel.js';

export class UnitView extends Component {
  constructor({ params, query } = { params: {} }) {
    super();
    this.propertyId = params.id;
    this.unitId = params.unitId;
    this.state = {
      unit: null,
      property: null,
      loading: true,
      error: null,
      editing: false,
      draft: null,
      busy: false,
      tab: UnitView._normalizeTab(query && query.tab),
    };
    this._panels = [];
  }

  // Detail-page tabs. 'details' (read-only data + edit form) is first, mérők
  // second per request, then dokumentumok / tulajdonosok. Each non-details
  // tab mounts its own per-unit panel into a slot.
  static TABS = ['details', 'meters', 'documents', 'owners'];
  static _normalizeTab(tab) {
    return UnitView.TABS.includes(tab) ? tab : 'details';
  }

  // Switch tabs in place: update state + the URL (?tab=) without going
  // through the router (replaceState doesn't fire hashchange), so the page
  // doesn't remount/refetch. Mirrors PropertyView._setTab.
  _setTab(tab) {
    const next = UnitView._normalizeTab(tab);
    if (next === this.state.tab) return;
    if (typeof window !== 'undefined' && window.history) {
      try {
        const url = new URL(window.location.href);
        url.hash = `#/properties/${encodeURIComponent(this.propertyId)}/units/${encodeURIComponent(this.unitId)}?tab=${encodeURIComponent(next)}`;
        window.history.replaceState(null, '', url.toString());
      } catch { /* jsdom may throw on replaceState */ }
    }
    this.setState({ tab: next });
  }

  get _propHref() { return `#/properties/${encodeURIComponent(this.propertyId)}`; }
  // Property-level default currency — used as the inherit fallback when
  // the unit doesn't carry its own override.
  get _propertyCurrency() { return (this.state.property?.currency || 'HUF').toUpperCase(); }
  // Effective currency the unit actually transacts in: its own override
  // wins, otherwise the property's default.
  get _currency() {
    const own = this.state.unit?.currency;
    return (own ? String(own) : this._propertyCurrency).toUpperCase();
  }
  get _canManage() {
    const p = this.state.property;
    return !!(p && auth.user && p.owner_user_id === auth.user.id);
  }

  template() {
    const u = this.state.unit;
    const title = u ? (u.identifier || '—') : '…';
    return `
      <section class="stack">
        <a href="${this._propHref}" class="back-link">${icon('arrow-left', { size: 'sm' })}<span>${esc(t('unit.back') || 'Vissza az ingatlanhoz')}</span></a>
        ${this.state.error ? `<div class="error-banner">${esc(this.state.error)}</div>` : ''}
        ${this.state.loading ? `<div class="muted">${esc(t('property.loading') || 'Betöltés…')}</div>` : `
          <div class="panel">
            <div class="panel-head">
              <div class="panel-num">${icon(unitIcon(u?.type), {})}</div>
              <div class="panel-titles">
                <div class="panel-title-eyebrow">${esc(t('property.units_title') || 'Egység')}</div>
                <h2 class="panel-title">${esc(title)}</h2>
              </div>
              <div class="panel-status">${this._availBadge()}</div>
            </div>
          </div>
          ${u ? `
            ${this._tabsHtml()}
            <div class="tab-panel" data-tab-panel="${esc(this.state.tab)}">
              ${this._tabContentHtml(u)}
            </div>
          ` : ''}
        `}
      </section>
    `;
  }

  _availBadge() {
    const u = this.state.unit;
    if (!u) return '';
    // Occupied units name their tenant on the badge (same as UnitCard).
    const who = u.current_tenant_name ? `${esc(u.current_tenant_name)} · ` : '';
    if (u.current_tenancy_open_ended) return `<span class="badge badge-warning">${who}${esc(t('property.unit_row.occupied_open_ended') || 'Foglalt')}</span>`;
    if (u.current_tenancy_end_date) return `<span class="badge badge-warning">${who}${esc(t('property.unit_row.occupied_until', { date: u.current_tenancy_end_date }) || 'Foglalt')}</span>`;
    return `<span class="badge success">${esc(t('property.wizard.units.available') || 'Szabad')}</span>`;
  }

  _detailsHtml() {
    const u = this.state.unit;
    if (!u) return '';
    const cur = this._currency;

    // Full street + city address, assembled from the structured parts.
    const addr = [
      [u.addr_street, u.addr_house_number].filter(Boolean).join(' '),
      u.addr_building ? `${u.addr_building} ép.` : '',
      u.addr_staircase ? `${u.addr_staircase} lph.` : '',
      u.floor ? `${u.floor}. em.` : '',
      u.addr_door ? `${u.addr_door}. ajtó` : '',
    ].filter(Boolean).join(' ');
    const cityLine = [u.postal_code, u.city, u.country].filter(Boolean).join(' ');
    const fullAddr = [addr, cityLine].filter(Boolean).join(', ');

    const money = (v) => (v != null && v !== '' ? formatMoney(Number(v), cur) : '—');
    const size = u.size_sqm != null && u.size_sqm !== '' ? `${formatSize(u.size_sqm)} m²` : '—';

    // Headline figures a landlord scans for first — shown as stat tiles.
    const stats = [
      [t('property.unit_form.asking_rent') || 'Bérleti díj / hó', money(u.asking_rent)],
      [t('property.unit_form.kozos_koltseg') || 'Közös költség / hó', money(u.kozos_koltseg)],
      [t('property.unit_form.size') || 'Méret', size],
      [t('property.unit_form.room_count') || 'Szobaszám', u.room_count || '—'],
    ];

    const furnished = u.furnished
      ? (t('property.unit_form.furnished_' + String(u.furnished).replace('partly_furnished', 'partly').replace('fully_furnished', 'fully')) || u.furnished)
      : '—';
    const energy = [
      u.energy_rating, u.energy_certificate_number,
      u.energy_certificate_expires_at ? String(u.energy_certificate_expires_at).slice(0, 10) : '',
    ].filter(Boolean).join(' · ') || '—';
    const basics = [
      [t('property.unit_form.type') || 'Típus', t('property.unit_form.types.' + u.type) || u.type],
      [t('property.unit_form.helyrajzi_szam') || 'Helyrajzi szám', u.helyrajzi_szam || '—'],
      [t('property.unit_form.furnished') || 'Bútorozottság', furnished],
      [t('property.unit_form.max_occupants') || 'Max. létszám', u.max_occupants != null && u.max_occupants !== '' ? String(u.max_occupants) : '—'],
      [t('property.wizard.steps.energy') || 'Energetika', energy],
    ];

    const grid = (pairs) => `<dl class="detail-grid">${pairs
      .map(([k, v]) => `<div class="detail-row"><dt>${esc(k)}</dt><dd>${esc(v)}</dd></div>`)
      .join('')}</dl>`;
    const block = (label, body) => `
      <div class="spec-block">
        <span class="eyebrow spec-eyebrow">${esc(label)}</span>
        ${body}
      </div>`;

    return `
      <div class="spec-stats">
        ${stats.map(([k, v]) => `
          <div class="spec-stat">
            <div class="spec-stat-l">${esc(k)}</div>
            <div class="spec-stat-v">${esc(v)}</div>
          </div>`).join('')}
      </div>
      ${block(t('unit.spec.basics') || 'Alapadatok', grid(basics))}
      ${block(t('unit.spec.address') || 'Cím', `<div class="spec-address">${esc(fullAddr || '—')}</div>`)}
      ${u.notes ? block(t('unit.notes_label') || 'Megjegyzések', `<div class="spec-notes">${esc(u.notes)}</div>`) : ''}
      ${this._canManage ? `
        <div class="spec-actions">
          ${u.availability_status === 'occupied' && u.current_tenancy_id
            ? `<a class="btn btn-primary" href="#/tenancies/${esc(u.current_tenancy_id)}">${icon('file-text', { size: 'sm' })}<span>${esc(t('unit.open_tenancy') || 'Bérleti szerződés')}</span></a>`
            : `<a class="btn btn-primary" href="#/tenancies/new?property=${esc(this.propertyId)}&amp;unit=${esc(this.unitId)}">${icon('user-plus', { size: 'sm' })}<span>${esc(t('move_in.entry'))}</span></a>`}
          <button type="button" class="btn btn-ghost" data-action="edit-unit">${icon('edit', { size: 'sm' })}<span>${esc(t('common.edit') || 'Szerkesztés')}</span></button>
          <button type="button" class="btn btn-ghost" data-action="delete-unit">${icon('trash', { size: 'sm' })}<span>${esc(t('unit.delete') || 'Egység törlése')}</span></button>
        </div>` : ''}
    `;
  }

  _formHtml() {
    const draft = this.state.draft || unitDraftFromRow(this.state.unit || {});
    return unitFormHtml({
      newUnit: draft,
      currency: this._currency,
      busy: this.state.busy,
      showAddAnother: false,
      chrome: false,
      saveLabel: t('common.save') || 'Mentés',
    });
  }

  _tabsHtml() {
    const tab = this.state.tab;
    const defs = [
      ['details', t('unit.tabs.details') || 'Adatok'],
      ['meters', t('property.wizard.steps.meters') || 'Mérők'],
      ['documents', t('property.tabs.documents') || 'Dokumentumok'],
      ['owners', t('property.wizard.steps.owners') || 'Tulajdonosok'],
    ];
    return `
      <div class="tabs" role="tablist">
        ${defs.map(([key, label]) => `
          <button type="button" class="tab${tab === key ? ' is-active' : ''}" role="tab"
                  aria-selected="${tab === key ? 'true' : 'false'}" data-tab="${key}">
            <span>${esc(label)}</span>
          </button>`).join('')}
      </div>`;
  }

  // Only the active tab's content is rendered; mount() mounts the matching
  // per-unit panel into the slot. The details tab keeps the .panel card chrome
  // around the read-only grid / edit form.
  _tabContentHtml(u) {
    switch (this.state.tab) {
      case 'meters':
        return `<div data-unit-meters-slot="${esc(u.id)}"></div>`;
      case 'documents':
        return `<div data-unit-documents-slot="${esc(u.id)}"></div>`;
      case 'owners':
        return `<div data-unit-external-owners-slot="${esc(u.id)}"></div>`;
      case 'details':
      default:
        return `<div class="panel"><div class="panel-body">${this.state.editing ? this._formHtml() : this._detailsHtml()}</div></div>`;
    }
  }

  mount() {
    const on = (sel, evt, fn) => this.el.querySelectorAll(sel).forEach((el) => el.addEventListener(evt, fn));

    if (this.state.loading && this.state.unit === null && !this._loadStarted) {
      this._loadStarted = true;
      this._load();
    }

    on('[data-tab]', 'click', (e) => this._setTab(e.currentTarget.getAttribute('data-tab')));
    on('[data-action="edit-unit"]', 'click', () => this.setState({ editing: true, error: null }));
    on('[data-action="delete-unit"]', 'click', () => this._delete());

    const form = this.el.querySelector('[data-unit-form]');
    if (form) {
      wireUnitFormApartmentOnly(form);
      // Optional unit address override: blank country = inherit from
      // the parent property, so requireDefault:false.
      wireAddressFields(form, { requireDefault: false });
      // Per-unit currency override picker: keep the rent/közös label
      // suffix in sync with the dropdown; inherit fallback is the
      // property's default currency.
      wireUnitCurrencySuffix(form, this._propertyCurrency);
      wireFurnishedRadio(form);
      wireEnergyRatingInputs(form);
      wireEnergyCertVerify(form, {
        onMatch: (row) => {
          if (row?.expirationDate) {
            const inp = form.querySelector('[name="energy_certificate_expires_at"]');
            if (inp && !inp.value) inp.value = String(row.expirationDate).slice(0, 10);
          }
        },
      });
      // Amenities chips toggle in place; unitPayloadFromForm reads them
      // back off the DOM via readUnitFormIntoDraft.
      wireAmenitiesChips(form, {
        initial: Array.isArray(this.state.draft?.amenities) ? this.state.draft.amenities : [],
      });
      on('[data-action="submit-unit-form"]', 'click', () => this._save(form));
      on('[data-action="submit-unit-form-add-another"]', 'click', () => this._save(form));
      on('[data-action="cancel-unit-form"]', 'click', () => {
        this.setState({ editing: false, draft: this.state.unit ? unitDraftFromRow(this.state.unit) : null, error: null });
      });
    }

    // Mount only the active tab's per-unit panel, so background tabs don't
    // hold timers / blob URLs. Switching tabs re-renders and remounts the
    // new tab's panel.
    this._destroyPanels();
    if (this.state.unit) {
      const uid = this.state.unit.id;
      const cm = this._canManage;
      let p = null;
      if (this.state.tab === 'meters') p = attachUnitMetersPanel(this.el, { unitId: uid, canManage: cm });
      else if (this.state.tab === 'documents') p = attachUnitDocumentsPanel(this.el, { unitId: uid, canManage: cm });
      else if (this.state.tab === 'owners') p = attachUnitExternalOwnersPanel(this.el, { unitId: uid, canManage: cm });
      this._panels = p ? [p] : [];
    }
  }

  async _load() {
    try {
      const [property, unit] = await Promise.all([
        api.get(`/properties/${this.propertyId}`),
        api.get(`/units/${this.unitId}`),
      ]);
      this.setState({ property, unit, draft: unitDraftFromRow(unit), loading: false });
    } catch (err) {
      this.setState({ loading: false, error: err instanceof ApiError ? err.message : t('property.errors.load') });
    }
  }

  async _save(form) {
    if (this.state.busy) return;
    clearFormError(form);
    const payload = unitPayloadFromForm(form);
    if (payload._error) {
      showFormError(form, t('property.wizard.errors.unit_identifier_required') || t('property.unit_form.identifier'), { field: 'identifier' });
      return;
    }
    this.state.busy = true;
    try {
      const updated = await api.put(`/units/${encodeURIComponent(this.unitId)}`, payload);
      this.state.busy = false;
      this.setState({ unit: updated, draft: unitDraftFromRow(updated), editing: false });
    } catch (err) {
      this.state.busy = false;
      showFormError(form, err instanceof ApiError ? err.message : (t('property.errors.save') || 'Mentés sikertelen'));
    }
  }

  async _delete() {
    if (!confirm(t('unit.delete_confirm') || 'Biztosan törlöd ezt az egységet?')) return;
    try {
      await api.delete(`/units/${encodeURIComponent(this.unitId)}`);
      window.location.hash = this._propHref.slice(1);
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : (t('property.errors.save') || 'Törlés sikertelen') });
    }
  }

  _destroyPanels() {
    for (const p of this._panels) { try { p?.destroy?.(); } catch {} }
    this._panels = [];
  }

  destroy() {
    this._destroyPanels();
    super.destroy();
  }
}
